/**
 * Send DCF a corrected packet (document correction, 2026-10-14).
 *
 * A packet went to DCF carrying a document that is not this resident's, and that document has
 * since been marked "Not this resident's". The server offers one act: open a corrected re-send.
 * It is NOT a DCF resubmission (DCF asked for nothing); it reuses the same episode machinery,
 * and the packet panel above prepares, reviews and sends it as it does any packet.
 */
import React, { useRef, useState } from 'react';
import { umedApi, freshKey } from './umedApi';
import type { CaseDetail } from './umedApi';
import { outcomeUnknown } from '../documents/NotThisResidents';

/** PR #420 round 4: an answer that never came back (or a 5xx) is an unknown outcome; the form keeps its key, so a retry replays. */
const RESEND_UNKNOWN = 'eMerge could not confirm whether the corrected re-send was opened. Reload the case before trying again.';
const WITHDRAW_UNKNOWN = 'eMerge could not confirm whether the corrected re-send was withdrawn. Reload the case before trying again.';
const REASON_MIN = 3;
const REASON_MAX = 500;

const COMMENT_MAX = 300;

type Info = NonNullable<CaseDetail['corrected_resend']>;

export default function CorrectedResend({ caseId, info, canOpen, onChanged }: {
  caseId: string;
  info: Info | null | undefined;
  canOpen: boolean;
  onChanged: (message: string) => void | Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef<string>(freshKey());
  if (!info || (!info.offered && !info.open)) return null;

  if (info.open) {
    return (
      <div className="um-section" aria-label="Corrected re-send">
        <h3>Send DCF a corrected packet</h3>
        <p className="um-notice">{`A corrected re-send is open: it replaces ${info.replaces ? info.replaces.words : 'the packet that went out'}. Prepare, review and send it from the packet panel above.`}</p>
        {/* Keyed by the re-send: a new one gets a fresh form and a fresh idempotency key (PR #420 X1). */}
        {canOpen && <WithdrawResend key={info.open.eventId} caseId={caseId} eventId={info.open.eventId} onChanged={onChanged} />}
      </div>
    );
  }

  const text = comment === null ? (info.suggestedComment || '') : comment;
  const r = info.replaces;
  return (
    <div className="um-section" aria-label="Corrected re-send">
      <h3>Send DCF a corrected packet</h3>
      {r && <p>{`Packet v${r.packetVersion}, sent ${shortSent(r)} by ${r.methods.length ? methodsWords(r) : 'a recorded sending'}, carried a document that is not this resident's.`}</p>}
      {canOpen ? (
        <form className="um-form" aria-label="Open a corrected re-send" onSubmit={async (e) => {
          e.preventDefault(); if (busy || !reason.trim()) return; setBusy(true); setErr(null);
          try {
            const res = await umedApi.openCorrectedResend(caseId, { reason: reason.trim(), cover_comment: text.trim() || undefined, idempotency_key: key.current });
            // PR #420 round 4: no status (never reached) or a 5xx (maybe after the commit) is an unknown outcome; the key is kept.
            if (!res.success) { setErr(outcomeUnknown(res) ? RESEND_UNKNOWN : (res.error || res.message || (res.code ? `Refused (${res.code}).` : 'That did not go through.'))); return; }
            key.current = freshKey();
            await onChanged('Corrected re-send opened.');
          } catch { setErr(RESEND_UNKNOWN); } finally { setBusy(false); }
        }}>
          <label><span>Why a corrected packet (required)</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={2} aria-label="Reason for the corrected re-send" />
          </label>
          <label><span>Cover comment for DCF</span>
            <textarea value={text} onChange={(e) => setComment(e.target.value)} maxLength={COMMENT_MAX} rows={3} aria-label="Cover comment" />
          </label>
          <p className="um-quiet" aria-live="polite">{`${text.length} of ${COMMENT_MAX}`}</p>
          <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || !reason.trim()}>{busy ? 'Opening…' : 'Open the corrected re-send'}</button></div>
          {err && <p className="um-notice stop" role="alert">{err}</p>}
        </form>
      ) : (
        <p className="um-quiet">Someone who prepares submissions can open the corrected re-send.</p>
      )}
    </div>
  );
}

/**
 * Ruling 2026-10-15: withdraw a corrected re-send opened by mistake, with a reason. Offered to whoever may open one (the
 * server applies the same gate, and refuses while a fax of the case is in flight or one of the re-send's faxes waits for
 * a person -- its words are shown as they come). The key is kept until the server answers, so a retry replays.
 */
function WithdrawResend({ caseId, eventId, onChanged }: { caseId: string; eventId: string; onChanged: (message: string) => void | Promise<void> }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef<string>(freshKey());
  const trimmed = reason.trim();
  const valid = trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX;
  if (!asking) {
    return <div className="btns"><button type="button" className="um-open" onClick={() => setAsking(true)}>Withdraw this corrected re-send</button></div>;
  }
  return (
    <form className="um-form" aria-label="Withdraw the corrected re-send" onSubmit={async (e) => {
      e.preventDefault(); if (busy || !valid) return; setBusy(true); setErr(null);
      try {
        const res = await umedApi.withdrawCorrectedResend(caseId, { reason: trimmed, event_id: eventId, idempotency_key: key.current });
        if (!res.success) { setErr(outcomeUnknown(res) ? WITHDRAW_UNKNOWN : (res.error || res.message || (res.code ? `Refused (${res.code}).` : 'That did not go through.'))); return; }
        key.current = freshKey();
        await onChanged('Corrected re-send withdrawn.');
      } catch { setErr(WITHDRAW_UNKNOWN); } finally { setBusy(false); }
    }}>
      <p className="um-quiet">Withdraw it only if it was opened by mistake. Nothing is sent to DCF, and a new corrected re-send can be opened later.</p>
      <label><span>{`Why it is withdrawn (required, ${REASON_MIN} to ${REASON_MAX} characters)`}</span>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={REASON_MAX} rows={2} aria-label="Reason for withdrawing" />
      </label>
      <div className="btns">
        <button type="submit" className="um-open um-primary" disabled={busy || !valid}>{busy ? 'Withdrawing…' : 'Withdraw the corrected re-send'}</button>
        <button type="button" className="um-open" disabled={busy} onClick={() => { setAsking(false); setErr(null); }}>Keep it open</button>
      </div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}

// `words` is the server's own phrase, "packet v3, sent 10/07/2026 by upload and fax". Reuse its pieces so the sentence matches.
function shortSent(r: NonNullable<Info['replaces']>): string {
  const m = /sent (\d{2}\/\d{2}\/\d{4})/.exec(r.words || '');
  return m ? m[1] : r.sentOn;
}
function methodsWords(r: NonNullable<Info['replaces']>): string {
  const m = /by (.+)$/.exec(r.words || '');
  return m ? m[1] : r.methods.join(' and ');
}
